import { SUPER_ADMIN_BASE_SERVICE } from "../utils/ApiUrls";


class SuperAdminServices {

    async getAllAccountRequests(token, params = {}) {
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page);
      if (params.page_size) queryParams.append('page_size', params.page_size);
      if (params.search) queryParams.append('search', params.search);
      if (params.status) queryParams.append('status', params.status);
      if (params.ordering) queryParams.append('ordering', params.ordering);

      const response = await fetch(
        `${SUPER_ADMIN_BASE_SERVICE}/account-requests/?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch account requests');
      }

      return await response.json();
    };

    async getAccountRequestDetail (token, requestId) {
      const response = await fetch(
        `${SUPER_ADMIN_BASE_SERVICE}/account-requests/${requestId}/`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch account request details');
      }

      return await response.json();
    };


    async updateAccountRequestStatus (token, requestId, status){
      try {
        const response = await fetch(`${SUPER_ADMIN_BASE_SERVICE}/account-requests/${requestId}/status-update/`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            progress_status: status
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Échec de la mise à jour du statut');
        }

        return await response.json();
      } catch (error) {
        console.error('Error updating account request status:', error);
        throw error;
      }
    }



    /**
     * Create a new organization with admin user
     * @param {Object} organizationData - Organization data
     * @param {string} organizationData.organisation_name - Name of the organization
     * @param {string} organizationData.email - Email of the admin user
     * @param {string} organizationData.first_name - First name of admin user
     * @param {string} organizationData.last_name - Last name of admin user
     * @param {string} organizationData.organisation_description - Description of organization (optional)
     * @returns {Promise} - Response from the server
     */
      async createOrganization(organizationData, accessToken) {
          try {
              const response = await fetch(`${SUPER_ADMIN_BASE_SERVICE}/organizations/create`, {
                  method: "POST",
                  headers: {
                      "Content-Type": "application/json",
                      "Authorization": `Bearer ${accessToken}`,
                  },
                  body: JSON.stringify(organizationData),
              });

              const data = await response.json();

              if (!response.ok) {
                  throw new Error(data.error || "Failed to create organization");
              }

              console.log("🏢 Organization created:", data);
              return { success: true, status: 201, data };

          } catch (error) {
              console.error("❌ Creation failed:", error);
              return { success: false, status: 500, error: error.message };
          }
      }


    /**
     * Update organization information including subscription details and activation status
     * @param {string} accessToken - JWT access token
     * @param {Object} organizationData - Organization update data
     * @param {number} organizationData.organization_id - ID of the organization to update (required)
     * @param {string} organizationData.organisation_name - Organization name (optional)
     * @param {string} organizationData.organisation_description - Organization description (optional)
     * @param {string} organizationData.subscription_date - Subscription start date (optional)
     * @param {string} organizationData.end_subscription_date - Subscription end date (optional)
     * @param {boolean} organizationData.is_subscribed - Subscription status (optional)
     * @param {boolean} organizationData.is_active - Organization active status (optional)
     * @param {string} organizationData.deactivation_reason - Reason for deactivation (optional)
     * @returns {Promise} Promise resolving to operation result with:
     *   - success: Boolean indicating operation success
     *   - message: Success message
     *   - organization: Updated organization information
     *   - updated_fields: Array of fields that were changed
     *   - changes_summary: Object with before/after values
     *   - action_performed_by: Admin who performed the action
     *   - timestamp: When the action was performed
     */
async updateOrganization(accessToken, organizationData) {
    if (!organizationData.organization_id) {
        throw new Error("organization_id is required");
    }

    try {
        const response = await fetch(`${SUPER_ADMIN_BASE_SERVICE}/organizations/update/`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${accessToken}`,
            },
            body: JSON.stringify(organizationData),
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || `Failed to update organization (${response.status})`);
        }

        console.log("🏢 Organization updated:", data);
        return { success: true, status: 200, data };

    } catch (error) {
        console.error("❌ Update failed:", error);
        return { success: false, status: 500, error: error.message };
    }
}



 getAllOrganizations(accessToken, params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.search) queryParams.append('search', params.search);
    if (params.is_subscribed !== undefined && params.is_subscribed !== '') {
      queryParams.append('is_subscribed', params.is_subscribed);
    }
    if (params.ordering) queryParams.append('ordering', params.ordering);
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    
    const queryString = queryParams.toString();
    const url = `${SUPER_ADMIN_BASE_SERVICE}/all-organizations/${queryString ? `?${queryString}` : ''}`;
    
    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            throw new Error(err.error || 'Failed to fetch organizations');
          });
        }
        return response.json();
      })
      .catch(error => {
        console.error('Error fetching organizations:', error);
        throw error;
      });
  }

  /**
   * Search organizations available for new subscriptions
   * @param {string} accessToken - JWT access token
   * @param {string} searchQuery - Search query (minimum 2 characters)
   * @param {number} pageSize - Number of results to return (default: 10, max: 20)
   * @returns {Promise} Promise resolving to available organizations list
   */
  searchAvailableOrganizations(accessToken, searchQuery, pageSize = 10) {
    console.log('🔍 Searching available organizations:', { searchQuery, pageSize });
    
    if (!searchQuery || searchQuery.length < 2) {
      return Promise.resolve({
        success: false,
        error: 'Search query must be at least 2 characters long'
      });
    }

    const queryParams = new URLSearchParams();
    queryParams.append('search', searchQuery);
    queryParams.append('page_size', pageSize);

    return fetch(`${SUPER_ADMIN_BASE_SERVICE}/organizations/search-available/?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            throw new Error(err.error || `Failed to search available organizations (${response.status})`);
          }).catch(() => {
            throw new Error(`Failed to search available organizations (${response.status})`);
          });
        }
        return response.json();
      })
      .then(data => {
        console.log('✅ Available organizations found:', data);
        return {
          success: true,
          status: 200,
          data: data
        };
      })
      .catch(error => {
        console.error('❌ Error searching available organizations:', error);
        return {
          success: false,
          status: 500,
          error: error.message
        };
      });
  }

  /**
   * Get comprehensive organization details including statistics and admin users
   * @param {string} accessToken - JWT access token
   * @param {number|string} orgId - Organization ID
   * @returns {Promise} Promise resolving to organization details with:
   *   - organization: Basic organization info
   *   - subscription: Complete subscription details with status and days remaining
   *   - statistics: Counts for users, risks, activities, processes
   *   - admin_users: Array of admin users with contact info
   *   - admin_users_count: Number of admin users
   */
  getOrganizationDetails(accessToken, orgId) {
    if (!orgId) {
      return Promise.reject(new Error('Organization ID is required'));
    }

    return fetch(`${SUPER_ADMIN_BASE_SERVICE}/organizations/details?id=${orgId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            const errorMessage = err.error || `Failed to fetch organization details (${response.status})`;
            throw new Error(errorMessage);
          }).catch(() => {
            // If response.json() fails, create a generic error
            throw new Error(`Failed to fetch organization details (${response.status})`);
          });
        }
        return response.json();
      })
      .then(data => {
        // Validate response structure
        if (!data.organization || !data.statistics || !data.subscription) {
          throw new Error('Invalid response format from server');
        }
        return data;
      })
      .catch(error => {
        console.error('Error fetching organization details:', error);
        throw error;
      });
  }

  /**
   * Get all users across all organizations with filtering and pagination
   * @param {string} accessToken - JWT access token
   * @param {Object} params - Query parameters for filtering and pagination
   * @param {string} params.search - Search by email, first_name, or last_name
   * @param {string} params.organization - Filter by organization ID or name
   * @param {string} params.is_active - Filter by active status (true/false)
   * @param {string} params.ordering - Sort by field (email, first_name, last_name, created_at, organization)
   * @param {number} params.page - Page number
   * @param {number} params.page_size - Items per page
   * @returns {Promise} Promise resolving to paginated user list with organization and role info
   */
  getAllUsers(accessToken, params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.search) queryParams.append('search', params.search);
    if (params.organization) queryParams.append('organization', params.organization);
    if (params.is_active !== undefined && params.is_active !== '') {
      queryParams.append('is_active', params.is_active);
    }
    if (params.ordering) queryParams.append('ordering', params.ordering);
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    
    const queryString = queryParams.toString();
    const url = `${SUPER_ADMIN_BASE_SERVICE}/all-users/${queryString ? `?${queryString}` : ''}`;
    
    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            const errorMessage = err.error || `Failed to fetch users (${response.status})`;
            throw new Error(errorMessage);
          }).catch(() => {
            throw new Error(`Failed to fetch users (${response.status})`);
          });
        }
        return response.json();
      })
      .then(data => {
        // Validate response structure
        if (!data.results || typeof data.count !== 'number') {
          throw new Error('Invalid response format from server');
        }
        return data;
      })
      .catch(error => {
        console.error('Error fetching all users:', error);
        throw error;
      });
  }

  /**
   * Get detailed information about a specific user
   * @param {string} accessToken - JWT access token
   * @param {number|string} userId - User ID
   * @returns {Promise} Promise resolving to user details with:
   *   - user: Basic user information
   *   - organization: Complete organization details
   *   - roles: All roles with active/inactive status and statistics
   */
  getUserDetails(accessToken, userId) {
    if (!userId) {
      return Promise.reject(new Error('User ID is required'));
    }

    return fetch(`${SUPER_ADMIN_BASE_SERVICE}/users/details/?id=${userId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            const errorMessage = err.error || `Failed to fetch user details (${response.status})`;
            throw new Error(errorMessage);
          }).catch(() => {
            throw new Error(`Failed to fetch user details (${response.status})`);
          });
        }
        return response.json();
      })
      .then(data => {
        // Validate response structure
        if (!data.user || !data.roles) {
          throw new Error('Invalid response format from server');
        }
        return data;
      })
      .catch(error => {
        console.error('Error fetching user details:', error);
        throw error;
      });
  }

  /**
   * Get all risks across all organizations with filtering and pagination
   * @param {string} accessToken - JWT access token
   * @param {Object} params - Query parameters for filtering and pagination
   * @param {string} params.risk_name - Search by risk name
   * @param {string} params.organization - Filter by organization ID or name
   * @param {string} params.status - Filter by risk status (pending, approved, rejected)
   * @param {string} params.probability - Filter by probability (mineur, normal, majeur, critique)
   * @param {string} params.ordering - Sort by field
   * @param {number} params.page - Page number
   * @param {number} params.page_size - Items per page
   * @returns {Promise} Promise resolving to paginated risks list
   */
  getAllRisks(accessToken, params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.risk_name) queryParams.append('risk_name', params.risk_name);
    if (params.organization) queryParams.append('organization', params.organization);
    if (params.status) queryParams.append('status', params.status);
    if (params.probability) queryParams.append('probability', params.probability);
    if (params.ordering) queryParams.append('ordering', params.ordering);
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    
    const queryString = queryParams.toString();
    const url = `${SUPER_ADMIN_BASE_SERVICE}/all-risks/${queryString ? `?${queryString}` : ''}`;
    
    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            const errorMessage = err.error || `Failed to fetch risks (${response.status})`;
            throw new Error(errorMessage);
          }).catch(() => {
            throw new Error(`Failed to fetch risks (${response.status})`);
          });
        }
        return response.json();
      })
      .then(data => {
        // Validate response structure
        if (!data.results || typeof data.count !== 'number') {
          throw new Error('Invalid response format from server');
        }
        return data;
      })
      .catch(error => {
        console.error('Error fetching all risks:', error);
        throw error;
      });
  }

  /**
   * Get detailed information about a specific risk
   * @param {string} accessToken - JWT access token
   * @param {number|string} riskId - Risk ID
   * @returns {Promise} Promise resolving to risk details with:
   *   - risk: Basic risk information
   *   - organization: Complete organization details
   *   - created_by: Creator information
   *   - last_updated_by: Last updater information
   *   - actions: Risk actions with assignments and statistics
   */
  getRiskDetails(accessToken, riskId) {
    if (!riskId) {
      return Promise.reject(new Error('Risk ID is required'));
    }

    return fetch(`${SUPER_ADMIN_BASE_SERVICE}/risks/details/?id=${riskId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            const errorMessage = err.error || `Failed to fetch risk details (${response.status})`;
            throw new Error(errorMessage);
          }).catch(() => {
            throw new Error(`Failed to fetch risk details (${response.status})`);
          });
        }
        return response.json();
      })
      .then(data => {
        // Validate response structure
        if (!data.risk || !data.actions) {
          throw new Error('Invalid response format from server');
        }
        return data;
      })
      .catch(error => {
        console.error('Error fetching risk details:', error);
        throw error;
      });
  }

  /**
   * Get all activities across all organizations with filtering and pagination
   * @param {string} accessToken - JWT access token
   * @param {Object} params - Query parameters for filtering and pagination
   * @param {string} params.activity_name - Search by activity name
   * @param {string} params.organization - Filter by organization ID or name
   * @param {string} params.status - Filter by activity status (Active, Inactive, etc.)
   * @param {string} params.ordering - Sort by field
   * @param {number} params.page - Page number
   * @param {number} params.page_size - Items per page
   * @returns {Promise} Promise resolving to paginated activities list
   */
  getAllActivities(accessToken, params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.activity_name) queryParams.append('activity_name', params.activity_name);
    if (params.organization) queryParams.append('organization', params.organization);
    if (params.status) queryParams.append('status', params.status);
    if (params.ordering) queryParams.append('ordering', params.ordering);
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    
    const queryString = queryParams.toString();
    const url = `${SUPER_ADMIN_BASE_SERVICE}/all-activities/${queryString ? `?${queryString}` : ''}`;
    
    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            const errorMessage = err.error || `Failed to fetch activities (${response.status})`;
            throw new Error(errorMessage);
          }).catch(() => {
            throw new Error(`Failed to fetch activities (${response.status})`);
          });
        }
        return response.json();
      })
      .then(data => {
        // Validate response structure
        if (!data.results || typeof data.count !== 'number') {
          throw new Error('Invalid response format from server');
        }
        return data;
      })
      .catch(error => {
        console.error('Error fetching all activities:', error);
        throw error;
      });
  }

  /**
   * Get detailed information about a specific activity
   * @param {string} accessToken - JWT access token
   * @param {number|string} activityId - Activity ID
   * @returns {Promise} Promise resolving to activity details with:
   *   - activity: Basic activity information
   *   - organization: Complete organization details
   *   - created_by: Creator information
   *   - last_updated_by: Last updater information
   *   - risks: Related risks with statistics
   */
  getActivityDetails(accessToken, activityId) {
    if (!activityId) {
      return Promise.reject(new Error('Activity ID is required'));
    }

    return fetch(`${SUPER_ADMIN_BASE_SERVICE}/activities/details/?id=${activityId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            const errorMessage = err.error || `Failed to fetch activity details (${response.status})`;
            throw new Error(errorMessage);
          }).catch(() => {
            throw new Error(`Failed to fetch activity details (${response.status})`);
          });
        }
        return response.json();
      })
      .then(data => {
        // Validate response structure
        if (!data.activity || !data.risks) {
          throw new Error('Invalid response format from server');
        }
        return data;
      })
      .catch(error => {
        console.error('Error fetching activity details:', error);
        throw error;
      });
  }

  /**
   * Get all processes across all organizations with filtering and pagination
   * @param {string} accessToken - JWT access token
   * @param {Object} params - Query parameters for filtering and pagination
   * @param {string} params.process_name - Search by process name
   * @param {string} params.organization - Filter by organization ID or name
   * @param {string} params.status - Filter by process status
   * @param {string} params.focal_point - Filter by focal point
   * @param {string} params.ordering - Sort by field
   * @param {number} params.page - Page number
   * @param {number} params.page_size - Items per page
   * @returns {Promise} Promise resolving to paginated processes list
   */
  getAllProcesses(accessToken, params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.process_name) queryParams.append('process_name', params.process_name);
    if (params.organization) queryParams.append('organization', params.organization);
    if (params.status) queryParams.append('status', params.status);
    if (params.focal_point) queryParams.append('focal_point', params.focal_point);
    if (params.ordering) queryParams.append('ordering', params.ordering);
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    
    const queryString = queryParams.toString();
    const url = `${SUPER_ADMIN_BASE_SERVICE}/all-processes/${queryString ? `?${queryString}` : ''}`;
    
    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            const errorMessage = err.error || `Failed to fetch processes (${response.status})`;
            throw new Error(errorMessage);
          }).catch(() => {
            throw new Error(`Failed to fetch processes (${response.status})`);
          });
        }
        return response.json();
      })
      .then(data => {
        // Validate response structure
        if (!data.results || typeof data.count !== 'number') {
          throw new Error('Invalid response format from server');
        }
        return data;
      })
      .catch(error => {
        console.error('Error fetching all processes:', error);
        throw error;
      });
  }

  /**
   * Get detailed information about a specific process
   * @param {string} accessToken - JWT access token
   * @param {number|string} processId - Process ID
   * @returns {Promise} Promise resolving to process details with:
   *   - process: Basic process information
   *   - organization: Complete organization details
   *   - created_by: Creator information
   *   - last_updated_by: Last updater information
   *   - activities: Related activities with statistics
   *   - tasks: Related tasks with statistics
   *   - reduction_actions: Process reduction actions
   */
  getProcessDetails(accessToken, processId) {
    if (!processId) {
      return Promise.reject(new Error('Process ID is required'));
    }

    return fetch(`${SUPER_ADMIN_BASE_SERVICE}/processes/details/?id=${processId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            const errorMessage = err.error || `Failed to fetch process details (${response.status})`;
            throw new Error(errorMessage);
          }).catch(() => {
            throw new Error(`Failed to fetch process details (${response.status})`);
          });
        }
        return response.json();
      })
      .then(data => {
        // Validate response structure
        if (!data.process || !data.activities || !data.tasks) {
          throw new Error('Invalid response format from server');
        }
        return data;
      })
      .catch(error => {
        console.error('Error fetching process details:', error);
        throw error;
      });
  }

  /**
   * Get dashboard statistics for super admin home page
   * @param {string} accessToken - JWT access token
   * @param {Object} params - Query parameters for customization
   * @param {string} params.sort_by - Sort top organizations by 'users', 'processes', or 'risks' (default: 'users')
   * @returns {Promise} Promise resolving to dashboard statistics with:
   *   - basic_counts: Total organizations, users, active sessions, unsubscribed orgs
   *   - top_organizations: Top 5 organizations (sortable by users/processes/risks)
   *   - recent_history: 5 most recent history entries
   *   - expiring_subscriptions: 5 organizations with subscription expiring soon
   */
  getDashboardStatistics(accessToken, params = {}) {
    console.log('📊 Fetching dashboard statistics:', params);
    
    const queryParams = new URLSearchParams();
    
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    
    const queryString = queryParams.toString();
    const url = `${SUPER_ADMIN_BASE_SERVICE}/dashboard/statistics/${queryString ? `?${queryString}` : ''}`;
    
    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            const errorMessage = err.error || `Failed to fetch dashboard statistics (${response.status})`;
            throw new Error(errorMessage);
          }).catch(() => {
            throw new Error(`Failed to fetch dashboard statistics (${response.status})`);
          });
        }
        return response.json();
      })
      .then(data => {
        console.log('📊 Dashboard statistics received:', data);
        
        // Validate response structure
        if (!data.basic_counts || !data.top_organizations || !Array.isArray(data.recent_history) || !Array.isArray(data.expiring_subscriptions)) {
          throw new Error('Invalid response format from server');
        }
        return data;
      })
      .catch(error => {
        console.error('Error fetching dashboard statistics:', error);
        throw error;
      });
  }

  /**
   * Deactivate or reactivate a user account
   * @param {string} accessToken - JWT access token
   * @param {Object} userData - User deactivation data
   * @param {number} userData.user_id - ID of the user to deactivate/reactivate
   * @param {boolean} userData.is_active - true to activate, false to deactivate
   * @param {string} userData.reason - Optional reason for the action
   * @returns {Promise} Promise resolving to operation result with:
   *   - success: Boolean indicating operation success
   *   - message: Success message
   *   - user: Updated user information
   *   - action_performed_by: Admin who performed the action
   *   - timestamp: When the action was performed
   *   - reason: Reason for the action (if provided)
   */
  deactivateUser(accessToken, userData) {
    console.log('👤 Deactivating/Reactivating user:', userData);
    
    // Validate required fields
    if (!userData.user_id) {
      return Promise.reject(new Error('user_id is required'));
    }
    
    if (userData.is_active === undefined || userData.is_active === null) {
      return Promise.reject(new Error('is_active is required'));
    }
    
    if (typeof userData.is_active !== 'boolean') {
      return Promise.reject(new Error('is_active must be a boolean value'));
    }
    
    return fetch(`${SUPER_ADMIN_BASE_SERVICE}/users/deactivate/`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(userData),
    })
      .then(async response => {

        const responseBody = await response.text(); // read it once

        console.log("Raw response body:", responseBody);
        if (!response.ok) {
          let errorMessage = `Failed to Deactivate user (${response.status})`;

          try {
            const err = JSON.parse(responseBody);
            if (err.error) {
              errorMessage = err.error;
            }
          } catch (parseError) {
            console.warn("Response is not valid JSON");
          }

          throw new Error(errorMessage);
        }
        return response.json();
      })
      .then(data => {
        console.log('👤 User status updated successfully:', data);
        
        // Validate response structure
        if (!data.success || !data.user) {
          throw new Error('Invalid response format from server');
        }
        return data;
      })
      .catch(error => {
        console.error('Error updating user status:', error);
        throw error;
      });
  }


  /**
   * Create an organization directly from an account creation request
   * @param {string} accessToken - JWT access token
   * @param {number} requestId - ID of the AccountCreationRequest
   * @returns {Promise} Promise resolving to created organization and user info
   */
  async createOrganizationFromRequest(accessToken, requestId) {
    try {
      const response = await fetch(`${SUPER_ADMIN_BASE_SERVICE}/organizations/create-from-request/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ request_id: requestId }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `Failed to create organization from request (${response.status})`);
      }

      return { success: true, data };
    } catch (error) {
      console.error('❌ createOrganizationFromRequest failed:', error);
      return { success: false, error: error.message };
    }
  }

}

// Export a single instance
const superAdminServices = new SuperAdminServices();
export default superAdminServices;